import { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useWorkspace } from 'hooks/useWorkspace';
import cn from 'classnames';

import { ReactComponent as ThumbsUpIcon } from 'assets/thumb-icon.svg';
import { ReactComponent as ThumbsDownSelectedIcon } from 'assets/thumbs_down_selected.svg';
import { ReactComponent as ThumbsUpSelectedIcon } from 'assets/thumbs_up_selected.svg';

import { Button } from 'Components/Button';

import { setDesignReviewHistory } from 'Redux/Actions/designReviewActions';
import { setCurrentRecord } from 'Redux/Actions/historyActions';

import DesignReviewService from 'services/designReview';
import { trackEvent } from 'services/eventTracking';

import styles from './styles.module.scss';

const LikeAndDismiss = ({ dismiss, currentFeedback, data, setErrorMsg, setShowFeedback, setDismiss }) => {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [thumbsUpEvent, setThumbsUp] = useState(data.thumbs_up ?? null);
  const [thumbsDownEvent, setThumbsDown] = useState(data.thumbs_down ?? null);

  const designReviewHistory = useSelector(state => state.designReview.designReviewHistory);
  const currentRecord = useSelector(state => state.historyReducer.currentRecord);
  const authUser = useSelector(state => state.userStore);
  const { isViewer } = useWorkspace();
  const isReviewOwner = !isViewer && Boolean(authUser?.uuid && currentRecord?.owner?.uuid === authUser.uuid);

  const isFeedbackIncomplete
      = (thumbsUpEvent === null || thumbsUpEvent === undefined
        || thumbsDownEvent === null || thumbsDownEvent === undefined);

  if (!isReviewOwner) {
    return null;
  }

  return (
    <div className={cn(styles['bottom-container'], styles['footer'])}>
      <div className={styles['design-section-row']}>
        <div className={styles['design-section-helpful-box']}>
          {isFeedbackIncomplete
            ? (
              <>
                <ThumbsUpIcon
                  className={styles['thumbs-design']}
                  alt="thumbs Up"
                  onClick={() => handleReactReview('like')}
                />
                <ThumbsUpIcon
                  className={cn(styles['thumbs-design'], styles['rotate'])}
                  alt="thumbs Down"
                  onClick={() => handleReactReview('unlike')}
                />
              </>
            )
            : (
              <>
                {
                  thumbsUpEvent
                    ? (
                      <ThumbsUpSelectedIcon
                        className={styles['thumbs-design']}
                        onClick={() => handleReactReview(null)}
                      />
                    )
                    : (
                      <ThumbsDownSelectedIcon
                        className={styles['thumbs-design']}
                        onClick={() => handleReactReview(null)}
                      />
                    )
                }
              </>
            )}
        </div>
      </div>
      {isLoading
        ? <div className={styles['loader']} />
        : (
          <Button
            onClick={handleDismiss}
            className={cn(
              styles['dismiss-button']
            )}
          >
            {dismiss ? 'Put back' : 'Dismiss'}
          </Button>
        )}
    </div>
  );

  function handleReactReview(event) {
    // Validate feedback index - use extended range only for deeper insights
    const deeperInsightsCount = currentRecord?.extra_data?.post_processes?.deeper_insights?.feedback?.length || 0;
    const maxAllowedIndex = data.isDeeperInsight
      ? currentRecord?.final_feedback?.length + deeperInsightsCount
      : currentRecord?.final_feedback?.length;

    if (
      currentFeedback <= 0 || !currentRecord
    || !currentRecord.final_feedback
    || currentFeedback > maxAllowedIndex
    ) {
      setErrorMsg('Unable to perform action: Invalid feedback index or record.');

      return;
    }

    if (event) {
      trackEvent(`[click] ${event === 'like' ? 'like feedback' : 'unlike feedback'}`, {
        recordUpdated: currentRecord.uuid,
        feedbackUpdated: currentFeedback - 1,
      });
    }
    else {
      trackEvent(`[click] ${currentFeedback.thumbs_up ? 'undo like' : 'undo unlike'} `, {
        recordUpdated: currentRecord.uuid,
        feedbackUpdated: currentFeedback - 1,
      });
    }

    const thumbsUp = event === 'like' ? true : event === 'unlike' ? false : null;
    const thumbsDown = event === 'unlike' ? true : event === 'like' ? false : null;

    // Create a deep copy to avoid mutating Redux state
    const recordToUpdate = JSON.parse(JSON.stringify(currentRecord));

    // Handle deeper insights feedback
    if (data.isDeeperInsight) {
      const deeperInsights = recordToUpdate.extra_data?.post_processes?.deeper_insights?.feedback;

      if (deeperInsights && deeperInsights.length > 0) {
        // Use data.index if available (set by categorization logic), otherwise calculate from currentFeedback
        // currentFeedback is 1-based, deeper insights start after final_feedback
        const feedbackIndex = data.index !== undefined ? data.index : (currentFeedback - 1);
        const deeperInsightIndex = feedbackIndex - recordToUpdate.final_feedback.length;

        if (deeperInsightIndex >= 0 && deeperInsightIndex < deeperInsights.length) {
          const updatedFeedback = {
            ...deeperInsights[deeperInsightIndex],
            thumbs_up: thumbsUp,
            thumbs_down: thumbsDown,
          };

          recordToUpdate.extra_data.post_processes.deeper_insights.feedback[deeperInsightIndex] = updatedFeedback;
        }
      }
    }
    else {
      // Handle regular feedback
      const updatedFeedback = {
        ...(recordToUpdate.final_feedback[currentFeedback - 1] || {}),
        thumbs_up: thumbsUp,
        thumbs_down: thumbsDown,
      };

      recordToUpdate.final_feedback[currentFeedback - 1] = updatedFeedback;
    }

    DesignReviewService.updateReview(recordToUpdate.uuid, recordToUpdate, setErrorMsg)
      .then(responseData => {
        if (responseData) {
          dispatch(setCurrentRecord(responseData));

          const reviewIndex = designReviewHistory?.results?.findIndex(val => val.uuid === currentRecord.uuid);

          if (reviewIndex !== -1 && designReviewHistory?.results) {
            const updatedReviewHistory = {
              ...designReviewHistory,
              results: [...designReviewHistory.results],
            };

            updatedReviewHistory.results[reviewIndex] = responseData;
            dispatch(setDesignReviewHistory(updatedReviewHistory));
          }
          setThumbsUp(thumbsUp);
          setThumbsDown(thumbsDown);
          setErrorMsg('');
        }
      })
      .catch(error => {
        setErrorMsg(`Caught In Error: ${error.message || error}`);
        trackEvent('[error] unable to update like/unlike status', {
          recordUpdated: currentRecord.uuid,
          feedbackUpdated: currentFeedback - 1,
          error,
        });
      });
  }

  function handleDismiss() {
    // Validate feedback index - use extended range only for deeper insights
    const deeperInsightsCount = currentRecord?.extra_data?.post_processes?.deeper_insights?.feedback?.length || 0;
    const maxAllowedIndex = data.isDeeperInsight
      ? currentRecord?.final_feedback?.length + deeperInsightsCount
      : currentRecord?.final_feedback?.length;

    if (
      currentFeedback <= 0 || !currentRecord
    || !currentRecord.final_feedback
    || currentFeedback > maxAllowedIndex
    ) {
      setErrorMsg('Unable to perform action: Invalid feedback index or record.');

      return;
    }

    trackEvent(`[click] ${dismiss ? 'put back feedback' : 'dismiss feedback'}`, {
      recordUpdated: currentRecord.uuid,
      feedbackUpdated: currentFeedback - 1,
    });

    // Create a deep copy to avoid mutating Redux state
    const recordToUpdate = JSON.parse(JSON.stringify(currentRecord));

    // Handle deeper insights feedback
    if (data.isDeeperInsight) {
      const deeperInsights = recordToUpdate.extra_data?.post_processes?.deeper_insights?.feedback;

      if (deeperInsights && deeperInsights.length > 0) {
        // Use data.index if available (set by categorization logic), otherwise calculate from currentFeedback
        // currentFeedback is 1-based, deeper insights start after final_feedback
        const feedbackIndex = data.index !== undefined ? data.index : (currentFeedback - 1);
        const deeperInsightIndex = feedbackIndex - recordToUpdate.final_feedback.length;

        if (deeperInsightIndex >= 0 && deeperInsightIndex < deeperInsights.length) {
          const updatedFeedback = {
            ...deeperInsights[deeperInsightIndex],
            dismiss: !dismiss,
          };

          recordToUpdate.extra_data.post_processes.deeper_insights.feedback[deeperInsightIndex] = updatedFeedback;
        }
      }
    }
    else {
      // Handle regular feedback
      const updatedFeedback = {
        ...(recordToUpdate.final_feedback[currentFeedback - 1] || {}),
        dismiss: !dismiss,
      };

      recordToUpdate.final_feedback[currentFeedback - 1] = updatedFeedback;
    }

    setIsLoading(true);
    DesignReviewService.updateReview(recordToUpdate.uuid, recordToUpdate, setErrorMsg)
      .then(responseData => {
        if (responseData) {
          dispatch(setCurrentRecord(responseData));

          const reviewIndex = designReviewHistory?.results?.findIndex(val => val.uuid === currentRecord.uuid);

          if (reviewIndex !== -1 && designReviewHistory?.results) {
            const updatedReviewHistory = {
              ...designReviewHistory,
              results: [...designReviewHistory.results],
            };

            updatedReviewHistory.results[reviewIndex] = responseData;
            dispatch(setDesignReviewHistory(updatedReviewHistory));
          }

          setDismiss(!dismiss);
          setShowFeedback(dismiss);
          setErrorMsg('');
          setIsLoading(false);
        }
      })
      .catch(error => {
        setIsLoading(false);
        setErrorMsg(`Caught In Error: ${error.message || error}`);
        trackEvent(`[error] unable to ${dismiss ? 'put back' : 'dismiss'} feedback`, {
          recordUpdated: currentRecord.uuid,
          feedbackUpdated: currentFeedback - 1,
          error,
        });
      });
  }
};

LikeAndDismiss.propTypes = {
  dismiss: PropTypes.bool,
  currentFeedback: PropTypes.number,
  data: PropTypes.object,
  setErrorMsg: PropTypes.func,
  setShowFeedback: PropTypes.func,
  setDismiss: PropTypes.func,
};

export default LikeAndDismiss;
